/* ==========================================================================
   Theme JS - Lapphund Designs
   Vanilla JS modules for interactivity
   ========================================================================== */

(function () {
  'use strict';

  /* Prices print without trailing zeros ($29), while real cents stay ($26.10). Whole-dollar pricing, 2026-09-12. */
  window.lappyAmount = function (cents) {
    var a = (cents / 100).toFixed(2);
    return a.slice(-3) === '.00' ? a.slice(0, -3) : a;
  };

  /* ---------- Sticky Header ---------- */
  const StickyHeader = {
    init() {
      this.header = document.querySelector('[data-sticky-header]');
      if (!this.header) return;

      this.sentinel = document.createElement('div');
      this.sentinel.style.height = '1px';
      this.sentinel.style.position = 'absolute';
      this.sentinel.style.top = '0';
      this.sentinel.style.left = '0';
      this.sentinel.style.width = '100%';
      this.header.parentNode.insertBefore(this.sentinel, this.header);

      const observer = new IntersectionObserver(
        ([entry]) => {
          this.header.classList.toggle('header--scrolled', !entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(this.sentinel);
    }
  };

  /* ---------- Mobile Menu ---------- */
  const MobileMenu = {
    init() {
      this.drawer = document.querySelector('[data-mobile-drawer]');
      this.openBtn = document.querySelector('[data-mobile-open]');
      this.closeBtn = document.querySelector('[data-mobile-close]');
      this.overlay = document.querySelector('[data-mobile-overlay]');

      if (!this.drawer || !this.openBtn) return;
      this.drawer.inert = true;

      this.focusableEls = [];
      this.firstFocusable = null;
      this.lastFocusable = null;

      this.openBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
        if (e.key === 'Tab' && this.isOpen()) this.trapFocus(e);
      });
      // a phone rotated into the desktop layout, or a back-forward restore, never keeps the menu open
      const wide = window.matchMedia('(min-width: 1024px)');
      (wide.addEventListener ? wide.addEventListener('change', (e) => { if (e.matches && this.isOpen()) this.close(); }) : null);
      window.addEventListener('pageshow', (e) => { if (e.persisted && this.isOpen()) this.close(); });
      // tapping a coat head in the menu picks that coat for the whole site, then follows the link
      this.drawer.addEventListener('click', (e) => {
        const head = e.target.closest('[data-drawer-coat]');
        if (!head) return;
        const coat = head.getAttribute('data-drawer-coat');
        try { localStorage.setItem('lappy-coat', coat); } catch (err) {}
        document.documentElement.setAttribute('data-coat', coat);
        this.markCoat();
        document.dispatchEvent(new CustomEvent('lappy:coat', { detail: { coat: coat } }));
      });
    },

    open() {
      this.drawer.inert = false;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-active');
      document.body.classList.add('no-scroll');
      this.openBtn.setAttribute('aria-expanded', 'true');
      this.markCoat();
      this.drawer.scrollTop = 0;

      this.focusableEls = Array.from(
        this.drawer.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
      );
      this.firstFocusable = this.focusableEls[0];
      this.lastFocusable = this.focusableEls[this.focusableEls.length - 1];

      if (this.closeBtn) this.closeBtn.focus();
    },

    close() {
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-active');
      document.body.classList.remove('no-scroll');
      this.openBtn.setAttribute('aria-expanded', 'false');
      this.openBtn.focus();
      setTimeout(() => { if (!this.isOpen()) this.drawer.inert = true; }, 350);
    },

    isOpen() {
      return this.drawer.classList.contains('is-open');
    },

    // the coat picked on the home page is ringed in the menu and named in its label
    markCoat() {
      let coat = null;
      try { coat = localStorage.getItem('lappy-coat'); } catch (e) {}
      const word = this.drawer.querySelector('[data-drawer-coat-word]');
      let current = null;
      this.drawer.querySelectorAll('[data-drawer-coat]').forEach((a) => {
        const hit = a.getAttribute('data-drawer-coat') === coat;
        a.classList.toggle('is-current', hit);
        if (hit) { current = a; a.setAttribute('aria-current', 'true'); } else { a.removeAttribute('aria-current'); }
      });
      if (word) word.textContent = current ? current.getAttribute('data-coat-word') : '';
    },

    trapFocus(e) {
      if (e.shiftKey) {
        if (document.activeElement === this.firstFocusable) {
          e.preventDefault();
          this.lastFocusable.focus();
        }
      } else {
        if (document.activeElement === this.lastFocusable) {
          e.preventDefault();
          this.firstFocusable.focus();
        }
      }
    }
  };

  /* ---------- Search Drawer ---------- */
  const SearchDrawer = {
    init() {
      this.drawer = document.querySelector('[data-search-drawer]');
      this.toggleBtns = document.querySelectorAll('[data-search-toggle]');
      this.closeBtn = document.querySelector('[data-search-close]');
      this.input = this.drawer ? this.drawer.querySelector('input[type="search"]') : null;

      if (!this.drawer || !this.toggleBtns.length) return;

      this.toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => this.toggle());
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    },

    toggle() {
      this.isOpen() ? this.close() : this.open();
    },

    open() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.input) {
        setTimeout(() => this.input.focus(), 100);
      }
    },

    close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
    },

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }
  };

  /* ---------- Announcement Bar ---------- */
  const AnnouncementBar = {
    init() {
      this.bar = document.querySelector('[data-announcement-bar]');
      if (!this.bar) return;

      this.slides = this.bar.querySelectorAll('[data-announcement-slide]');
      if (this.slides.length <= 1) return;

      this.currentIndex = 0;
      this.autoRotate = this.bar.dataset.autoRotate === 'true';
      this.speed = parseInt(this.bar.dataset.speed, 10) * 1000 || 5000;
      this.timer = null;
      this.isPaused = false;

      const prevBtn = this.bar.querySelector('[data-announcement-prev]');
      const nextBtn = this.bar.querySelector('[data-announcement-next]');

      // WCAG 2.2.2: taking manual control stops the rotation for good, so the arrows are the
      // pause mechanism as well as the navigation.
      if (prevBtn) prevBtn.addEventListener('click', () => { this.stopAutoPlay(); this.prev(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { this.stopAutoPlay(); this.next(); });

      this.bar.addEventListener('mouseenter', () => { this.isPaused = true; });
      this.bar.addEventListener('mouseleave', () => { this.isPaused = false; });
      // keyboard parity with the hover pause
      this.bar.addEventListener('focusin', () => { this.isPaused = true; });
      this.bar.addEventListener('focusout', () => { this.isPaused = false; });

      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.autoRotate && !still) this.startAutoPlay();
    },

    show(index) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', i !== index);
      });
      this.currentIndex = index;
    },

    next() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.show(nextIndex);
    },

    prev() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.show(prevIndex);
    },

    startAutoPlay() {
      this.timer = setInterval(() => {
        if (!this.isPaused) this.next();
      }, this.speed);
    },

    stopAutoPlay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.autoRotate = false;
    }
  };

  /* ---------- Announcement Meter ---------- */
  /* The cart drawer's paw meter, said site-wide. While a cart is open the first slide becomes
     the progress line and a 3px fill runs under the bar's text; an empty cart restores the
     static shipping truth. The bar's row height is fixed, so nothing shifts. */
  const AnnouncementMeter = {
    THRESHOLD: 7500,

    init() {
      this.bar = document.querySelector('[data-announcement-bar]');
      if (!this.bar) return;

      this.slide = this.bar.querySelector('[data-announcement-slide]');
      if (!this.slide) return;
      this.text = this.slide.querySelector('a, span');
      if (!this.text) return;

      this.original = this.text.textContent;
      this.meter = this.bar.querySelector('[data-announcement-meter]');
      this.fill = this.bar.querySelector('[data-announcement-meter-fill]');
      this.paws = this.meter
        ? Array.from(this.meter.querySelectorAll('.announcement-bar__meter-paw'))
        : [];

      this.refresh();
      window.addEventListener('cart:updated', () => this.refresh());
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
    },

    money(cents) {
      return '$' + window.lappyAmount(Math.max(cents, 0));
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return;
        this.render(await res.json());
      } catch (err) {
        // an offline or blocked cart fetch leaves the static shipping line in place
      }
    },

    render(cart) {
      const total = cart.total_price || 0;
      const hasBowl = (cart.items || []).some((item) => (item.product_type || '').indexOf('Bowl') > -1);

      if (!cart.item_count) {
        this.text.textContent = this.original;
        this.setMeter(null);
        return;
      }

      if (total >= this.THRESHOLD) {
        this.text.textContent = hasBowl
          ? 'Free shipping unlocked. Dog bowls ship separately.'
          : 'Free shipping unlocked';
        this.setMeter(100);
        return;
      }

      this.text.textContent = "You're " + this.money(this.THRESHOLD - total) + ' from free Canada & US shipping';
      this.setMeter(Math.round((total / this.THRESHOLD) * 100));
    },

    setMeter(pct) {
      if (!this.meter) return;
      if (pct === null) {
        this.meter.hidden = true;
        return;
      }
      this.meter.hidden = false;
      if (this.fill) this.fill.style.width = pct + '%';
      this.paws.forEach((paw, i) => paw.classList.toggle('is-reached', pct >= (i + 1) * 25));
    }
  };

  /* ---------- Forced downloads ---------- */
  const ForceDownload = {
    init() {
      document.addEventListener('click', async (e) => {
        const link = e.target.closest('[data-download]');
        if (!link) return;
        const url = link.getAttribute('href');
        if (!url) return;

        // the download attribute is ignored cross-origin, and the file is on the Shopify CDN, so
        // fetch it to a blob on this origin and save that instead
        e.preventDefault();
        const label = link.querySelector('[data-download-label]') || link;
        const original = label.textContent;
        label.textContent = link.getAttribute('data-download-busy') || 'Downloading...';

        try {
          const res = await fetch(url, { mode: 'cors' });
          if (!res.ok) throw new Error(res.status);
          const blob = await res.blob();
          const href = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = href;
          a.download = link.getAttribute('data-download') || url.split('/').pop().split('?')[0];
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(href), 30000);
        } catch (err) {
          // a blocked fetch should not cost the reader the file, so fall back to opening it
          window.open(url, '_blank', 'noopener');
        } finally {
          label.textContent = original;
        }
      });
    }
  };

  /* ---------- Animation Observer ---------- */
  const AnimationObserver = {
    observer: null,
    reduced: false,

    init() {
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.reduced) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                this.observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
        );
      }

      this.watch(document);

      // Sections that swap their own markup in (the Halloween row restoring its default cards,
      // the coat bands) insert elements long after this ran. Without this, those arrive carrying
      // data-animate, nothing ever observes them, and they sit at opacity 0 for good.
      if ('MutationObserver' in window) {
        new MutationObserver((records) => {
          records.forEach(r => r.addedNodes.forEach(n => {
            if (n.nodeType === 1) this.watch(n);
          }));
        }).observe(document.body, { childList: true, subtree: true });
      }
    },

    watch(root) {
      const els = [];
      if (root.nodeType === 1 && root.hasAttribute('data-animate')) els.push(root);
      if (root.querySelectorAll) els.push(...root.querySelectorAll('[data-animate]'));

      els.forEach(el => {
        if (el.classList.contains('is-visible')) return;
        if (this.reduced || !this.observer) {
          el.classList.add('is-visible');
          return;
        }
        this.observer.observe(el);
      });
    }
  };

  /* ---------- Product Card ---------- */
  const ProductCard = {
    init() {
      document.addEventListener('click', (e) => {
        const quickAddBtn = e.target.closest('[data-quick-add]');
        if (!quickAddBtn) return;

        e.preventDefault();
        const variantId = quickAddBtn.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId, quickAddBtn);
        }
      });
    },

    async addToCart(variantId, btn) {
      btn.classList.add('btn--loading');
      btn.disabled = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        });

        if (!res.ok) throw new Error('Add to cart failed');

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        CartUpdater.updateCartCount(cart.item_count);

        // Notify listeners (e.g. the announcement bar's shipping meter)
        window.dispatchEvent(new CustomEvent('cart:updated'));

        // Open cart drawer after quick add with celebration
        if (CartDrawer.drawer) {
          await CartDrawer.refresh();
          CartDrawer.open(true, btn);
        }
        // Brief success feedback
        const originalText = btn.textContent;
        btn.textContent = 'Added! ✓';
        btn.classList.add('btn--success');
        setTimeout(() => {
          btn.textContent = originalText;
          btn.classList.remove('btn--success');
        }, 1500);
      } catch (err) {
        console.error('Quick add error:', err);
      } finally {
        btn.classList.remove('btn--loading');
        btn.disabled = false;
      }
    }
  };

  /* ---------- Variant Selector ---------- */
  const VariantSelector = {
    init() {
      const form = document.querySelector('[data-product-form]');
      if (!form) return;

      this.form = form;
      this.productData = null;

      const dataEl = document.querySelector('[data-product-json]');
      if (dataEl) {
        try {
          this.productData = JSON.parse(dataEl.textContent);
        } catch (e) {
          return;
        }
      }

      if (!this.productData) return;

      this.optionCount = this.productData.options.length;

      // Find which option index is the color option
      this.colorOptionIndex = -1;
      for (var ci = 0; ci < this.productData.options.length; ci++) {
        var name = (this.productData.options[ci] || '').toString().toLowerCase();
        if (name === 'color' || name === 'colour') {
          this.colorOptionIndex = ci;
          break;
        }
      }

      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-variant-option]')) {
          // Update button selected states for radio inputs
          if (e.target.type === 'radio') {
            const wrapper = e.target.closest('.product-option__values');
            if (wrapper) {
              wrapper.querySelectorAll('.product-option__btn').forEach(function(btn) { btn.classList.remove('is-selected'); });
              var label = e.target.closest('label');
              if (label) {
                var btn = label.querySelector('.product-option__btn');
                if (btn) btn.classList.add('is-selected');
              }
            }
          }

          // Update the option value label text
          var optionWrapper = e.target.closest('.product-option');
          if (optionWrapper) {
            var valueLabel = optionWrapper.querySelector('[data-option-value]');
            if (valueLabel) valueLabel.textContent = e.target.value;
          }

          this.onOptionChange();
        }
      });

      // When a thumbnail is clicked, detect the color from its filename and
      // sync the dropdown to match, so dropdown + gallery stay linked.
      this.bindThumbnailSync();

      // On page load, switch the main image to match the currently selected
      // color dropdown value (instead of always showing featured_image).
      this.syncInitialImage();
    },

    bindThumbnailSync() {
      if (this.colorOptionIndex === -1) return;
      var self = this;
      var thumbs = document.querySelectorAll('.product-gallery__thumb');
      thumbs.forEach(function(thumb, idx) {
        thumb.addEventListener('click', function() {
          self.syncDropdownFromThumb(idx);
        });
      });
    },

    syncDropdownFromThumb(thumbIndex) {
      if (this.colorOptionIndex === -1) return;
      if (!this.productData.media || !this.productData.media[thumbIndex]) return;

      var media = this.productData.media[thumbIndex];
      var src = media.src || (media.preview_image && media.preview_image.src) || '';
      var filename = src.split('/').pop().split('?')[0].toLowerCase();
      var alt = (media.alt || '').toLowerCase().trim();

      // Try to detect the color from this image
      var colorSelect = this.form.querySelector('select[data-option-index="' + this.colorOptionIndex + '"]');
      if (!colorSelect) return;

      var options = colorSelect.querySelectorAll('option');
      var matchedColor = null;

      // Check each color option against the filename and alt text
      options.forEach(function(opt) {
        if (matchedColor) return;
        var colorSlug = opt.value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
        var colorLower = opt.value.toLowerCase().trim();
        if (alt === colorLower || filename.indexOf(colorSlug) > -1) {
          matchedColor = opt.value;
        }
      });

      if (matchedColor && colorSelect.value !== matchedColor) {
        colorSelect.value = matchedColor;
        // Update the label text
        var optionWrapper = colorSelect.closest('.product-option');
        if (optionWrapper) {
          var valueLabel = optionWrapper.querySelector('[data-option-value]');
          if (valueLabel) valueLabel.textContent = matchedColor;
        }
        // Update the variant (price, URL, availability)
        this.onOptionChange();
      }
    },

    syncInitialImage() {
      // On page load, switch the gallery to match the selected dropdown color
      // so the main image matches the dropdown instead of always showing featured_image
      var selectedOptions = this.getSelectedOptions();
      var variant = this.productData.variants.find(function(v) {
        return v.options.every(function(opt, i) { return opt === selectedOptions[i]; });
      });
      if (variant) {
        this.switchVariantImage(variant);
      }
    },

    getSelectedOptions() {
      var options = [];
      for (var i = 0; i < this.optionCount; i++) {
        // Check for select elements first (color dropdown)
        var select = this.form.querySelector('select[data-option-index="' + i + '"]');
        if (select) {
          options.push(select.value);
          continue;
        }
        // Check for radio inputs (size buttons etc)
        var radios = this.form.querySelectorAll('input[data-option-index="' + i + '"]');
        if (radios.length) {
          var checked = Array.from(radios).find(function(r) { return r.checked; });
          options.push(checked ? checked.value : radios[0].value);
        }
      }
      return options;
    },

    onOptionChange() {
      var selectedOptions = this.getSelectedOptions();
      var variant = this.productData.variants.find(function(v) {
        return v.options.every(function(opt, i) { return opt === selectedOptions[i]; });
      });

      if (variant) {
        this.updateVariant(variant);
      }
    },

    updateVariant(variant) {
      var idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;

      var priceEl = document.querySelector('[data-product-price]');
      if (priceEl) {
        priceEl.innerHTML = this.formatMoney(variant.price);
      }

      var comparePriceEl = document.querySelector('[data-compare-price]');
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.innerHTML = this.formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      var addBtn = this.form.querySelector('[data-add-to-cart]');
      var addBtnText = this.form.querySelector('[data-add-to-cart-text]');
      if (addBtn && addBtnText) {
        if (variant.available) {
          addBtn.disabled = false;
          addBtnText.textContent = addBtnText.dataset.addText || 'Add to Cart';
        } else {
          addBtn.disabled = true;
          addBtnText.textContent = addBtnText.dataset.soldText || 'Sold Out';
        }
      }

      // Update the main product image to show the variant image
      this.switchVariantImage(variant);

      var url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    },

    formatMoney(cents) {
      var amount = window.lappyAmount(cents);
      return window.Shopify && window.Shopify.currency
        ? window.Shopify.currency.symbol + amount
        : '$' + amount;
    },

    switchVariantImage(variant) {
      // Find the color option value for this variant
      var colorValue = null;
      if (this.productData.options) {
        for (var oi = 0; oi < this.productData.options.length; oi++) {
          var optName = (this.productData.options[oi] || '').toString().toLowerCase();
          if (optName === 'color' || optName === 'colour') {
            colorValue = variant.options[oi];
            break;
          }
        }
      }

      if (!colorValue) return;

      var thumbs = document.querySelectorAll('.product-gallery__thumb');
      if (!thumbs.length) return;

      var colorLower = colorValue.toLowerCase().trim();
      var colorSlug = colorLower.replace(/[^a-z0-9]+/g, '-');
      var targetIndex = -1;

      // Use product.media from JSON to find the matching image by filename.
      // Filenames from print-on-demand services (Printful etc) contain the color
      // name as a slug, e.g. "unisex-staple-t-shirt-red-front-697ca187afc6e.jpg"
      if (this.productData.media && this.productData.media.length > 0) {
        for (var i = 0; i < this.productData.media.length; i++) {
          var media = this.productData.media[i];
          var src = media.src || (media.preview_image && media.preview_image.src) || '';
          var filename = src.split('/').pop().split('?')[0].toLowerCase();
          var alt = (media.alt || '').toLowerCase().trim();

          // Check exact alt text match first (e.g. alt="Black")
          if (alt === colorLower) {
            targetIndex = i;
            break;
          }
          // Check filename contains color slug (e.g. "athletic-heather" in filename)
          if (filename.indexOf(colorSlug) > -1) {
            targetIndex = i;
            break;
          }
        }
      }

      // Fallback: check thumbnail DOM elements directly
      if (targetIndex === -1) {
        thumbs.forEach(function(thumb, idx) {
          if (targetIndex > -1) return;
          var thumbImg = thumb.querySelector('img');
          if (!thumbImg) return;
          var thumbFile = (thumbImg.src || '').split('/').pop().split('?')[0].toLowerCase();
          if (thumbFile.indexOf(colorSlug) > -1) {
            targetIndex = idx;
          }
        });
      }

      if (targetIndex > -1 && thumbs[targetIndex]) {
        thumbs[targetIndex].click();
      } else {
        console.log('[VariantSelector] No image match for color "' + colorValue + '" (slug: ' + colorSlug + ')');
      }
    }
  };

  /* ---------- Quantity Selector ---------- */
  const QuantitySelector = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-btn]');
        if (!btn) return;

        const wrapper = btn.closest('[data-qty-wrapper]');
        const input = wrapper ? wrapper.querySelector('[data-qty-input]') : null;
        if (!input) return;

        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 999;

        if (btn.dataset.qtyBtn === 'minus') {
          input.value = Math.max(min, current - 1);
        } else {
          input.value = Math.min(max, current + 1);
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };

  /* ---------- Accordion ---------- */
  const Accordion = {
    init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-accordion-trigger]');
        if (!trigger) return;

        const item = trigger.closest('[data-accordion-item]');
        if (!item) return;

        const content = item.querySelector('[data-accordion-content]');
        const isOpen = item.classList.contains('is-open');

        if (isOpen) {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          if (content) content.style.maxHeight = '0';
        } else {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          if (content) content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    }
  };

  /* ---------- Cart Updater ---------- */
  const CartUpdater = {
    init() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-product-form]');
        if (!form) return;

        e.preventDefault();
        this.handleAddToCart(form);
      });

      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (!input) return;

        // On cart page, don't auto-update, user clicks "Update Cart" button instead
        if (input.closest('#cart-update-form')) {
          document.querySelector('[data-cart-update]')?.classList.add('cart-update-btn--visible');
          return;
        }

        const lineKey = input.dataset.cartQty;
        const qty = parseInt(input.value, 10);
        this.updateItem(lineKey, qty);
      });

      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (!removeBtn) return;

        e.preventDefault();
        const lineKey = removeBtn.dataset.cartRemove;
        this.updateItem(lineKey, 0);
      });
    },

    async handleAddToCart(form) {
      const submitBtn = form.querySelector('[data-add-to-cart]');
      if (submitBtn) {
        submitBtn.classList.add('btn--loading');
        submitBtn.disabled = true;
      }

      try {
        const formData = new FormData(form);
        const data = {
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity'), 10) || 1
        };

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.description || 'Could not add to cart');
        }

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        this.updateCartCount(cart.item_count);

        // Notify listeners (e.g. coaster showcase tracker)
        window.dispatchEvent(new CustomEvent('cart:updated'));

        // Open cart drawer with celebration after add to cart
        if (CartDrawer.drawer) {
          await CartDrawer.refresh();
          CartDrawer.open(true, submitBtn);
        } else if (submitBtn) {
          const textEl = submitBtn.querySelector('[data-add-to-cart-text]');
          if (textEl) {
            const original = textEl.textContent;
            textEl.textContent = 'Added!';
            setTimeout(() => { textEl.textContent = original; }, 1500);
          }
        }
      } catch (err) {
        console.error('Add to cart error:', err);
        alert(err.message || 'Could not add item to cart. Please try again.');
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('btn--loading');
          submitBtn.disabled = false;
        }
      }
    },

    async updateItem(lineKey, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity })
        });

        if (!res.ok) throw new Error('Cart update failed');

        // Re-fetch full cart for accurate data (change.js can return stale data)
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        this.updateCartCount(cart.item_count);

        // Notify listeners (e.g. coaster showcase tracker)
        window.dispatchEvent(new CustomEvent('cart:updated'));

        // Always update cart page banners + totals if on cart page
        if (document.querySelector('[data-bundle-banner]') || document.querySelector('[data-coaster-banner]')) {
          this.updateCartPage(cart);
        }

        // Refresh cart drawer if open
        if (CartDrawer.isOpen()) {
          await CartDrawer.refresh();
          if (cart.item_count === 0) CartDrawer.close();
        } else if (quantity === 0 || cart.item_count === 0) {
          window.location.reload();
        }
      } catch (err) {
        console.error('Cart update error:', err);
      }
    },

    updateCartCount(count) {
      const badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach(badge => {
        badge.textContent = count;
        if (count > 0) {
          badge.removeAttribute('hidden');
          badge.classList.add('cart-count--animate');
          setTimeout(() => badge.classList.remove('cart-count--animate'), 300);
        } else {
          badge.setAttribute('hidden', '');
        }
      });
    },

    updateCartPage(cart) {
      // Update subtotal
      const subtotalEl = document.querySelector('[data-cart-subtotal]');
      if (subtotalEl) {
        const amount = window.lappyAmount(cart.total_price);
        subtotalEl.textContent = '$' + amount;
      }

      // Update line totals
      cart.items.forEach(item => {
        const lineTotal = document.querySelector('[data-line-total="' + item.key + '"]');
        if (lineTotal) {
          const amount = window.lappyAmount(item.final_line_price);
          lineTotal.textContent = '$' + amount;
        }
      });

      // Update free-shipping progress banner (isolated try/catch)
      try {
        const banner = document.querySelector('[data-bundle-banner]');
        if (banner) {
          var threshold = parseInt(banner.getAttribute('data-ship-threshold') || '7500', 10);
          var total = cart.total_price; // full subtotal in cents (matches the free-shipping discount)
          var hasBowl = cart.items.some(function(it){ return (it.product_type || '').toLowerCase().indexOf('bowl') !== -1; });
          banner.style.display = cart.item_count > 0 ? '' : 'none';
          const textEl = banner.querySelector('[data-bundle-text]');
          const fillEl = banner.querySelector('[data-bundle-fill]');
          const paws = banner.querySelectorAll('[data-bundle-paw]');

          if (textEl) {
            if (total >= threshold) {
              textEl.textContent = hasBowl ? '\uD83C\uDF89 Free shipping unlocked! Dog bowls ship separately.' : '\uD83C\uDF89 You\'ve unlocked FREE shipping!';
              textEl.classList.add('cart-bundle-banner__text--complete');
            } else {
              var remaining = (threshold - total) / 100;
              textEl.textContent = 'Add $' + window.lappyAmount(threshold - total) + ' more for FREE shipping!' + (hasBowl ? ' Dog bowls ship separately.' : '') + ' \uD83D\uDC3E';
              textEl.classList.remove('cart-bundle-banner__text--complete');
            }
          }

          if (fillEl) {
            fillEl.style.width = Math.min(100, Math.round(total / threshold * 100)) + '%';
          }

          paws.forEach(function(paw) {
            var pawNum = parseInt(paw.getAttribute('data-bundle-paw'), 10);
            paw.classList.toggle('is-reached', total >= threshold * pawNum / 3);
          });
        }
      } catch (e) { console.error('Free shipping banner update error:', e); }

      // Update coaster bundle banner (isolated try/catch)
      try {
        const coasterBanner = document.querySelector('[data-coaster-banner]');
        if (coasterBanner) {
          const idsStr = coasterBanner.getAttribute('data-coaster-ids') || '';
          const coasterIds = idsStr.split(',').filter(Boolean).map(Number);
          let coasterCount = 0;
          cart.items.forEach(function(item) {
            if (coasterIds.indexOf(item.product_id) > -1) coasterCount += item.quantity;
          });

          const cText = coasterBanner.querySelector('[data-coaster-banner-text]');
          const cFill = coasterBanner.querySelector('[data-coaster-banner-fill]');
          const cPaws = coasterBanner.querySelectorAll('[data-coaster-banner-paw]');

          if (cText) {
            if (coasterCount >= 4) {
              cText.textContent = 'Coaster set complete! \uD83C\uDF89 Your 20% coaster discount is unlocked.';
              cText.classList.add('cart-bundle-banner__text--complete');
            } else if (coasterCount === 3) {
              cText.textContent = 'So close! Just 1 more coaster to unlock 20% off your set! \uD83D\uDC3E';
              cText.classList.remove('cart-bundle-banner__text--complete');
            } else if (coasterCount === 2) {
              cText.textContent = '2 of 4 coasters \u2014 keep going to save 20% on your coaster set! \uD83D\uDC3E';
              cText.classList.remove('cart-bundle-banner__text--complete');
            } else if (coasterCount === 1) {
              cText.textContent = '1 of 4 coasters \u2014 add 3 more to save 20% on your coaster set! \uD83D\uDC3E';
              cText.classList.remove('cart-bundle-banner__text--complete');
            }
          }

          if (cFill) {
            cFill.style.width = Math.min(coasterCount / 4 * 100, 100) + '%';
          }

          cPaws.forEach(function(paw) {
            var pawNum = parseInt(paw.getAttribute('data-coaster-banner-paw'), 10);
            paw.classList.toggle('is-reached', pawNum <= coasterCount);
          });

          // Real applied savings, read from the actual cart (never a hardcoded promise)
          const discountMsg = document.querySelector('[data-discount-message]');
          if (discountMsg) {
            var savings = (cart.original_total_price || cart.total_price) - cart.total_price;
            if (savings > 0) {
              discountMsg.textContent = '\uD83C\uDF89 You\u2019re saving $' + window.lappyAmount(savings) + ', applied automatically';
              discountMsg.style.display = '';
            } else {
              discountMsg.style.display = 'none';
            }
          }
        }
      } catch (e) { console.error('Coaster banner update error:', e); }
    }
  };

  /* ---------- Cart Drawer ---------- */
  const CartDrawer = {
    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
      this.closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
      this.body = this.drawer.querySelector('[data-cart-drawer-body]');

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      // Allow opening drawer from cart icon click
      document.addEventListener('click', (e) => {
        const cartToggle = e.target.closest('[data-cart-drawer-toggle]');
        if (cartToggle) {
          e.preventDefault();
          this.open();
        }
      });
    },

    open(celebrate, trigger) {
      if (!this.drawer) return;
      // the add-to-cart button disables itself before this runs, which drops focus to body;
      // callers pass the button so focus can return to it on close
      const active = document.activeElement;
      this.trigger = trigger || ((active && active !== document.body) ? active : this.trigger);
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.setAttribute('aria-modal', 'true');
      document.body.classList.add('no-scroll');
      const panel = this.drawer.querySelector('[data-cart-drawer-panel]') || this.drawer;
      setTimeout(() => { (this.closeBtn || panel).focus(); }, 30);
      if (!this._trap) {
        this._trap = (e) => {
          if (e.key !== 'Tab' || !this.isOpen()) return;
          const els = Array.from(this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null);
          if (!els.length) return;
          const first = els[0], last = els[els.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
          else if (!this.drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', this._trap);
      }
      if (celebrate) this.celebrate();
    },

    celebrate() {
      const confetti = this.drawer.querySelector('[data-cart-confetti]');
      if (!confetti) return;
      confetti.classList.remove('is-active');
      void confetti.offsetWidth;
      confetti.classList.add('is-active');
      setTimeout(() => confetti.classList.remove('is-active'), 1600);
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.removeAttribute('aria-modal');
      document.body.classList.remove('no-scroll');
      if (this.trigger && typeof this.trigger.focus === 'function') { const t = this.trigger; setTimeout(() => { t.disabled = false; t.focus(); }, 40); }
    },

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-open');
    },

    async refresh() {
      if (!this.drawer || !this.body) return;
      try {
        const res = await fetch('/?section_id=cart-drawer');
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBody = doc.querySelector('[data-cart-drawer-body]');
        const newFooter = doc.querySelector('[data-cart-drawer-footer]');
        const newCount = doc.querySelector('[data-cart-drawer-count]');

        if (newBody) this.body.innerHTML = newBody.innerHTML;

        const currentFooter = this.drawer.querySelector('[data-cart-drawer-footer]');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        } else if (newFooter && !currentFooter) {
          this.drawer.querySelector('.cart-drawer__panel').appendChild(newFooter.cloneNode(true));
        } else if (!newFooter && currentFooter) {
          currentFooter.remove();
        }

        if (newCount) {
          var countEl = this.drawer.querySelector('[data-cart-drawer-count]');
          if (countEl) countEl.textContent = newCount.textContent;
        }

        // Refresh bundle tracker
        const newBundle = doc.querySelector('[data-bundle-tracker]');
        const currentBundle = this.drawer.querySelector('[data-bundle-tracker]');
        if (newBundle && currentBundle) {
          currentBundle.innerHTML = newBundle.innerHTML;
        } else if (newBundle && !currentBundle) {
          const header = this.drawer.querySelector('.cart-drawer__header');
          if (header) header.insertAdjacentElement('afterend', newBundle.cloneNode(true));
        } else if (!newBundle && currentBundle) {
          currentBundle.remove();
        }

        // Refresh coaster tracker
        const newCoaster = doc.querySelector('[data-coaster-tracker]');
        const currentCoaster = this.drawer.querySelector('[data-coaster-tracker]');
        if (newCoaster && currentCoaster) {
          currentCoaster.innerHTML = newCoaster.innerHTML;
        } else if (newCoaster && !currentCoaster) {
          const bundleEl = this.drawer.querySelector('[data-bundle-tracker]');
          if (bundleEl) {
            bundleEl.insertAdjacentElement('afterend', newCoaster.cloneNode(true));
          } else {
            const header = this.drawer.querySelector('.cart-drawer__header');
            if (header) header.insertAdjacentElement('afterend', newCoaster.cloneNode(true));
          }
        } else if (!newCoaster && currentCoaster) {
          currentCoaster.remove();
        }

        // Refresh mood indicator
        const newMood = doc.querySelector('.cart-drawer__mood');
        const currentMood = this.drawer.querySelector('.cart-drawer__mood');
        if (newMood && currentMood) currentMood.textContent = newMood.textContent;
      } catch (err) {
        console.error('Cart drawer refresh error:', err);
      }
    }
  };

  /* ---------- Instagram Feed (Behold) ---------- */
  const InstagramFeed = {
    init() {
      const grid = document.querySelector('[data-behold-feed]');
      if (!grid) return;

      const feedId = grid.dataset.beholdFeed;
      const count = parseInt(grid.dataset.beholdCount, 10) || 6;
      if (!feedId) return;

      fetch('https://feeds.behold.so/' + feedId)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => {
          const posts = (Array.isArray(data) ? data : data.posts || []).slice(0, count);
          if (!posts.length) {
            grid.style.display = 'none';
            return;
          }

          const frag = document.createDocumentFragment();
          posts.forEach((p) => {
            const sizes = p.sizes || {};
            const isVideo = (p.mediaType || '').toUpperCase() === 'VIDEO';
            const src =
              (isVideo && p.thumbnailUrl) ||
              (sizes.medium && sizes.medium.mediaUrl) ||
              (sizes.small && sizes.small.mediaUrl) ||
              p.mediaUrl ||
              p.thumbnailUrl ||
              '';
            if (!src) return;

            const item = document.createElement('div');
            item.className = 'instagram-feed__item';

            const a = document.createElement('a');
            a.href = p.permalink || '#';
            a.target = '_blank';
            a.rel = 'noopener';

            const img = document.createElement('img');
            img.src = src;
            img.alt = (p.prunedCaption || p.caption || 'Instagram post by Lapphund Designs')
              .toString()
              .slice(0, 120);
            img.loading = 'lazy';
            img.width = 400;
            img.height = 400;

            a.appendChild(img);
            item.appendChild(a);
            frag.appendChild(item);
          });

          grid.innerHTML = '';
          grid.appendChild(frag);
          grid.classList.add('is-loaded');
        })
        .catch(() => {
          // Feed unavailable: hide the grid, the Follow CTA below still shows.
          grid.style.display = 'none';
        });
    }
  };

  /* ---------- Hero Scroll Indicator ---------- */
  const HeroScroll = {
    init() {
      const btn = document.querySelector('[data-hero-scroll]');
      if (!btn) return;

      btn.addEventListener('click', () => {
        const section = btn.closest('.shopify-section') || btn.closest('.hero');
        const next = section ? section.nextElementSibling : null;
        const header = document.querySelector('[data-sticky-header]');
        const offset = header ? header.offsetHeight : 0;
        const top = next
          ? next.getBoundingClientRect().top + window.scrollY - offset
          : window.innerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }
  };

  /* ---------- Back to Top ---------- */
  const BackToTop = {
    init() {
      this.btn = document.querySelector('[data-back-to-top]');
      if (!this.btn) return;

      window.addEventListener('scroll', () => {
        this.btn.classList.toggle('is-visible', window.scrollY > 500);
      }, { passive: true });

      this.btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  /* ---------- Product Gallery (thumbnail → main image) ---------- */
  /* Horizontal swipe helper (mobile). Ignores vertical scroll + multi-touch. */
  function addSwipe(el, onLeft, onRight) {
    if (!el) return;
    let x0 = null, y0 = null;
    el.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { x0 = null; return; }
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0, dy = t.clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        if (dx < 0) onLeft(); else onRight();
      }
    }, { passive: true });
  }

  /* Full-screen image lightbox (built once, reused). */
  const Lightbox = {
    el: null, media: [], index: 0, onChange: null,
    build() {
      if (this.el) return;
      const x = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      const lt = '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>';
      const rt = '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
      const o = document.createElement('div');
      o.className = 'pgallery-lightbox';
      o.setAttribute('role', 'dialog');
      o.setAttribute('aria-modal', 'true');
      o.innerHTML =
        '<button class="pgallery-lightbox__close" aria-label="Close">' + x + '</button>' +
        '<button class="pgallery-lightbox__nav pgallery-lightbox__nav--prev" aria-label="Previous image">' + lt + '</button>' +
        '<figure class="pgallery-lightbox__stage"><img class="pgallery-lightbox__img" alt=""></figure>' +
        '<button class="pgallery-lightbox__nav pgallery-lightbox__nav--next" aria-label="Next image">' + rt + '</button>' +
        '<div class="pgallery-lightbox__counter"></div>';
      document.body.appendChild(o);
      this.el = o;
      this.imgEl = o.querySelector('.pgallery-lightbox__img');
      this.counterEl = o.querySelector('.pgallery-lightbox__counter');
      o.querySelector('.pgallery-lightbox__close').addEventListener('click', () => this.close());
      o.querySelector('.pgallery-lightbox__nav--prev').addEventListener('click', (e) => { e.stopPropagation(); this.go(-1); });
      o.querySelector('.pgallery-lightbox__nav--next').addEventListener('click', (e) => { e.stopPropagation(); this.go(1); });
      o.addEventListener('click', (e) => {
        if (e.target === o || e.target.classList.contains('pgallery-lightbox__stage')) this.close();
      });
      addSwipe(o, () => this.go(1), () => this.go(-1));
      document.addEventListener('keydown', (e) => {
        if (!this.el || !this.el.classList.contains('is-open')) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.go(1);
        else if (e.key === 'ArrowLeft') this.go(-1);
      });
    },
    open(media, index, onChange) {
      this.build();
      this.media = media || []; this.index = index || 0; this.onChange = onChange || null;
      if (!this.media.length) return;
      this.render();
      this.el.classList.add('is-open');
      document.body.classList.add('pgallery-lock');
    },
    render() {
      const m = this.media[this.index] || {};
      const hi = (m.src || '').replace(/width=\d+/, 'width=1600');
      this.imgEl.src = hi || m.src || '';
      this.imgEl.alt = m.alt || '';
      const multi = this.media.length > 1;
      this.counterEl.textContent = multi ? (this.index + 1) + ' / ' + this.media.length : '';
      this.el.querySelectorAll('.pgallery-lightbox__nav').forEach((n) => { n.style.display = multi ? '' : 'none'; });
      this.counterEl.style.display = multi ? '' : 'none';
    },
    go(dir) {
      if (this.media.length < 2) return;
      this.index = (this.index + dir + this.media.length) % this.media.length;
      this.render();
      if (this.onChange) this.onChange(this.index);
    },
    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      document.body.classList.remove('pgallery-lock');
    }
  };

  const ProductGallery = {
    init() {
      const gallery = document.querySelector('.product-gallery');
      if (!gallery) return;
      const mainImage = gallery.querySelector('[data-product-main-image]');
      if (!mainImage) return;
      const thumbs = Array.from(gallery.querySelectorAll('.product-gallery__thumb'));

      const media = thumbs.length
        ? thumbs.map((t) => ({ src: t.dataset.mediaSrc, srcset: t.dataset.mediaSrcset, alt: t.dataset.mediaAlt || '' }))
        : [{ src: mainImage.currentSrc || mainImage.src, srcset: mainImage.srcset, alt: mainImage.alt }];

      let current = 0;

      const showMain = (i) => {
        current = (i + media.length) % media.length;
        const m = media[current];
        if (m.src) mainImage.src = m.src;
        if (m.srcset) mainImage.srcset = m.srcset; else mainImage.removeAttribute('srcset');
        mainImage.alt = m.alt;
        thumbs.forEach((t, idx) => t.classList.toggle('is-active', idx === current));
        const active = thumbs[current];
        if (active) active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      };

      gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('.product-gallery__thumb');
        if (!thumb) return;
        showMain(thumbs.indexOf(thumb));
      });

      // Click the main image -> open the lightbox at the current image.
      mainImage.addEventListener('click', () => {
        Lightbox.open(media, current, (i) => showMain(i));
      });

      // Mobile: swipe the main image to move through the gallery.
      addSwipe(gallery.querySelector('.product-gallery__main'), () => showMain(current + 1), () => showMain(current - 1));
    }
  };

  /* ---------- Sticky ATC Bar (mobile) ---------- */
  const StickyATCBar = {
    init() {
      const stickyBar = document.querySelector('.product-sticky-bar');
      const mainATC = document.querySelector('[data-add-to-cart]');
      if (!stickyBar || !mainATC) return;

      const stickyBtn = stickyBar.querySelector('[data-sticky-atc]');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
          const form = document.querySelector('[data-product-form]');
          if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
        });
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      }, { threshold: 0 });

      observer.observe(mainATC);
    }
  };

  /* ---------- Corner Topic Filter (blog) ---------- */
  const CornerFilter = {
    init() {
      const bar = document.querySelector('[data-corner-filter]');
      const grid = document.querySelector('[data-corner-grid]');
      if (!bar || !grid) return;

      const empty = document.querySelector('[data-corner-empty]');
      const cards = Array.from(grid.querySelectorAll('.corner-card'));

      bar.addEventListener('click', (e) => {
        const chip = e.target.closest('.corner__chip');
        if (!chip) return;
        bar.querySelectorAll('.corner__chip').forEach((c) => c.classList.remove('is-active'));
        chip.classList.add('is-active');

        const tag = chip.dataset.tag;
        let shown = 0;
        cards.forEach((card) => {
          const tags = card.dataset.tags || '';
          const match = tag === '*' || tags.indexOf(',' + tag + ',') > -1;
          card.style.display = match ? '' : 'none';
          if (match) shown += 1;
        });
        if (empty) empty.hidden = shown !== 0;
      });
    }
  };


  /* ---------- Coat Marks: the visitor's coat, recognised on every surface ---------- */
  const CoatMarks = {
    init() {
      let coat = null;
      try { coat = localStorage.getItem('lappy-coat'); } catch (e) {}
      if (!coat) return;
      document.querySelectorAll('.product-card[data-coats]').forEach((card) => {
        if ((' ' + card.getAttribute('data-coats') + ' ').indexOf(' coat-' + coat + ' ') < 0) return;
        card.classList.add('is-your-coat');
        const m = card.querySelector('.coat-mark[data-coat-mark="' + coat + '"]'); if (m) m.classList.add('is-you');
      });
      const pd = document.querySelector('[data-product-coats]');
      if (pd && (' ' + pd.getAttribute('data-product-coats') + ' ').indexOf(' coat-' + coat + ' ') >= 0) {
        const you = pd.querySelector('[data-coat-you]'); if (you) you.hidden = false;
      }
    }
  };

  /* ---------- Initialize All Modules ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    StickyHeader.init();
    MobileMenu.init();
    SearchDrawer.init();
    AnnouncementBar.init();
    AnnouncementMeter.init();
    AnimationObserver.init();
    ForceDownload.init();
    ProductCard.init();
    // the gallery binds its thumbnail clicks before the variant selector syncs the initial image (LD-004)
    ProductGallery.init();
    VariantSelector.init();
    QuantitySelector.init();
    Accordion.init();
    CartUpdater.init();
    CartDrawer.init();
    // Expose cart modules for section-level scripts (e.g. coaster bundle builder)
    window.CartDrawer = CartDrawer;
    window.CartUpdater = CartUpdater;
    BackToTop.init();
    StickyATCBar.init();
    CoatMarks.init();
    HeroScroll.init();
    InstagramFeed.init();
    CornerFilter.init();
  });
})();
